/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FeatureResponse } from "@tivocorp/data-domain-lib";

/**
 * Reads a boolean attribute off a device `FeatureResponse`.
 *
 * Extracted so `DeviceFeatures` and `PostSigninInteractor` share one parsing
 * rule instead of duplicating it. Behaviour (preserved from the original
 * `PostSigninInteractor.readFeatureAttributeBoolean`):
 *   - native boolean attribute value → returned as-is
 *   - string `"true"` / `"false"` (trimmed, case-insensitive) → parsed
 *   - missing attribute, missing attributes array, non-boolean/non-string
 *     value, or any other unrecognised value → `fallbackValue`
 */
export function parseFeatureAttributeBoolean(
  feature: FeatureResponse,
  attributeName: string,
  fallbackValue: boolean
): boolean {
  const attributes = feature.attributes;
  if (!Array.isArray(attributes)) {
    return fallbackValue;
  }

  const attribute = attributes.find(
    (item) => item.attributeName === attributeName
  );
  if (attribute === undefined || attribute === null) {
    return fallbackValue;
  }

  const value: unknown = attribute.attributeValue;
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value !== "string") {
    return fallbackValue;
  }

  const normalizedValue: string = value.trim().toLowerCase();
  if (normalizedValue === "true") {
    return true;
  }
  if (normalizedValue === "false") {
    return false;
  }
  return fallbackValue;
}
